import { sortByIndex } from '@/Helpers/sorters';
import { randomString } from '@/Helpers/strings';
import { maxLength, minLength, required } from '@vuelidate/validators';
import InfoModel from './InfoModel';
import QuestionModel from './QuestionModel';
import StatusReportTypeModel from './StatusReportTypeModel';
import PromMetaModel from './PromMetaModel';

// @ts-check
export class PromModel {
  constructor({
    Id = randomString(),
    Language = '',
    Title = '',
    Concept = '',
    StatusReportTypes = [],
    LanguageIds = ['sv'],
    About = '',
    PromTypeOverride,
    Questions = [],
    QuestionCount = 0,
    Infos = [],
    Tags = [],
    HideScore = false,
    PrefillPreviousAnswers = false,
    Version = 0,
    OriginalName = '',
    Introduction = '',
    Image = null,
    PromMeta = null,
    Saved = true,
    IsVisible = false
  }) {
    this._modelName = 'Prom';
    this.Id = Id;
    this.Language = Language;
    this.Title = Title;
    this.Concept = Concept;
    this.StatusReportTypes = StatusReportTypes.map(
      t => new StatusReportTypeModel(t)
    );
    this.LanguageIds = LanguageIds;
    this.About = About;
    this.PromTypeOverride = PromTypeOverride || null;
    this.Questions = Questions.map(q => new QuestionModel(q)).sort(sortByIndex);
    this.QuestionCount =
      (Questions ? Questions.length : false) || QuestionCount;
    this.Infos = Infos.map(i => new InfoModel(i));
    this.Tags = Tags;
    this.HideScore = HideScore;
    this.PrefillPreviousAnswers = PrefillPreviousAnswers;
    this.Version = Version;
    this.OriginalName = OriginalName;
    this.Introduction = Introduction;
    this.Image = Image;
    this.PromMeta = PromMeta ? new PromMetaModel(PromMeta) : null;
    this._saved = Saved;
    this.IsVisible = IsVisible;
  }

  hasLanguageVersion(languageId) {
    return this.LanguageIds.includes(languageId);
  }

  get questionCount() {
    return this.Questions.filter(question => question.Answers.length > 0)
      .length;
  }

  get shouldGetFullProm() {
    return this.QuestionCount > this.Questions.length;
  }

  get sortedItems() {
    return this.Questions.concat(this.Infos).sort(sortByIndex);
  }

  get itemsByIndex() {
    var items = [];

    this.sortedItems.forEach(sortedItem => {
      if (sortedItem.ShowLastInfo) {
        const info = this.getPreviousInfoText(sortedItem);
        if (info) {
          items.push(info);
        }
      }
      items.push(sortedItem);
    });
    return items;
  }

  getPreviousInfoText(item) {
    const startIndex = this.sortedItems.indexOf(item);

    for (let i = startIndex; i > -1; i--) {
      if (this.sortedItems[i]._modelName === 'Info') {
        const info = new InfoModel({ ...this.sortedItems[i] });
        return info;
      }
    }
    return null;
  }

  get isFirstItemInfo() {
    if (!this.itemsByIndex[0]) {
      return undefined;
    }
    return this.itemsByIndex[0]._modelName === 'Info';
  }

  get isMetaLocked() {
    return this.Version > 0;
  }

  __copy() {
    return JSON.parse(JSON.stringify(this));
  }

  static get validations() {
    return {
      Title: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(512)
      },
      Concept: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(64)
      },
      About: {
        required,
        maxLength: maxLength(1024)
      },
      Id: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(32)
      },
      OriginalName: {
        required,
        maxLength: maxLength(128)
      },
      PromTypeOverride: {},
      Introduction: {
        minLength: minLength(16),
        maxLength: maxLength(1024)
      },
      PrefillPreviousAnswers: {},
      HideScore: {},
      IsVisible: {},
      StatusReportTypes: {},
      Questions: {
        required,
        minLength: minLength(1)
      },
      Tags: {},
      Image: {}
    };
  }
}

export default PromModel;
